import { useEffect, useState, useSyncExternalStore } from "react";
import { RefreshCw, Smartphone, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { pullPrefs, resetPrefs, subscribeSync, syncStatus, deviceLabel } from "@/lib/prefsSync";

const WHAT = ["Theme & colours", "Visualizer style", "Equalizer", "Crossfade & gapless", "Lyrics on/off & timing nudges", "Audiobook speed", "Folded menu groups", "Last-played queue"];
const ago = (iso) => {
  if (!iso) return "never";
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  return m < 1 ? "just now" : m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : new Date(iso).toLocaleDateString();
};

export const SyncSection = () => {
  const status = useSyncExternalStore(subscribeSync, syncStatus);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!status.at) pullPrefs().catch(() => {}); }, [status.at]);
  const syncNow = async () => {
    setBusy(true);
    try { await pullPrefs(); toast.success("Settings are up to date"); } catch { toast.error("Couldn't reach the server"); } finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm("Forget the synced settings on your account? Each device keeps what it has now, and nothing is shared until you change a setting again.")) return;
    try { await resetPrefs(); toast.success("Synced settings cleared"); } catch { toast.error("Couldn't clear settings"); }
  };
  const lp = status.player;
  return (
    <section className="mt-8 rounded-lg border border-zinc-200 bg-white p-8" data-testid="sync-section">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-sky-50 text-sky-700"><Smartphone size={18} /></div>
        <div>
          <h2 className="text-lg font-medium">Settings on every device</h2>
          <p className="text-xs text-zinc-500">Your preferences are saved to your account and follow you wherever you sign in. Newest change wins.</p>
        </div>
      </div>
      <ul className="mt-5 flex flex-wrap gap-2" data-testid="sync-what">
        {WHAT.map((w) => <li key={w} className="rounded-full bg-zinc-100 px-3 py-1 text-xs text-zinc-600">{w}</li>)}
      </ul>
      <div className="mt-5 grid gap-1 font-mono text-xs text-zinc-500">
        <p data-testid="sync-this-device">This device: {deviceLabel()}</p>
        <p data-testid="sync-last">Last synced: {ago(status.at)}</p>
        <p data-testid="sync-last-player">Last queue saved {lp?.device ? `from ${lp.device}` : "—"}{lp?.updated_at ? ` · ${ago(lp.updated_at)} · ${lp.count} songs` : ""}</p>
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <button onClick={syncNow} disabled={busy} data-testid="sync-now-button" className="flex items-center gap-2 rounded-full border border-zinc-300 px-5 py-2 text-sm transition-colors hover:border-zinc-900 disabled:opacity-50">
          <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Sync now
        </button>
        <button onClick={reset} data-testid="sync-reset-button" className="flex items-center gap-2 rounded-full px-5 py-2 text-sm text-red-700 transition-colors hover:bg-red-50">
          <Trash2 size={14} /> Clear synced settings
        </button>
      </div>
    </section>
  );
};
